"use client";

import Image from "next/image";
import { useState, useMemo, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { useCart } from "@/context/cart-context";
import { getAllProducts, buildSlug, type UnityProduct } from "@/lib/products";

/* ── Types ── */

interface QuizAnswers {
  category: string | null;       // "GEZICHT" | "OGEN" | "LIPPEN" | null (help me choose)
  undertone: string | null;      // "koel" | "warm" | "neutraal" | null
  look: string | null;           // "natural" | "romantic" | "bold" | "colorful"
  sensitivities: string[];       // multi-select
  productType: string | null;    // "individual" | "set" | "all"
}

interface ScoredProduct {
  product: UnityProduct;
  score: number;
  reasons: string[];
}

const TOTAL_STEPS = 5;

/* ── Animations ── */

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
  }),
};

const transition = {
  x: { type: "spring" as const, stiffness: 300, damping: 30 },
  opacity: { duration: 0.25 },
};

/* ── Price formatting ── */

const fmt = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" });

/* ── Palette subcategories (sets/palettes) ── */

const SET_SUBCATEGORIES = ["Oogschaduw Paletten"];
const SET_PRICE_THRESHOLD = 25;

/* ── Stars ── */

function Stars({ rating, count }: { rating: number; count: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} className="w-3.5 h-3.5" viewBox="0 0 20 20" fill={i <= Math.round(rating) ? "var(--star)" : "none"} stroke="var(--star)" strokeWidth="1.5">
          <path d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.49L10 14.27 5.06 16.7 6 11.21l-4-3.9 5.53-.8L10 1.5z" />
        </svg>
      ))}
      <span className="text-[12px] text-text-secondary ml-1">({count})</span>
    </div>
  );
}

/* ── Helpers ── */

function isIndividualProduct(p: UnityProduct): boolean {
  return !SET_SUBCATEGORIES.includes(p.subcategory) && parseFloat(p.price) < SET_PRICE_THRESHOLD;
}

function isSetProduct(p: UnityProduct): boolean {
  return SET_SUBCATEGORIES.includes(p.subcategory) || parseFloat(p.price) >= SET_PRICE_THRESHOLD;
}

function undertoneMatches(productUndertone: string, selectedUndertone: string): boolean {
  if (!productUndertone) return false;
  return productUndertone.toLowerCase().includes(selectedUndertone.toLowerCase());
}

function finishMatchesLook(finish: string, look: string): boolean {
  if (!finish) return false;
  const f = finish.toLowerCase();
  if (look === "natural") return f.includes("mat") || f.includes("mix");
  if (look === "romantic") return f.includes("zijdeglans") || f.includes("mix");
  if (look === "bold") return f.includes("zijdeglans") || f.includes("glinsterend");
  // colorful - all finishes welcome
  return true;
}

/* ── Build base pool (shared between quiz and Q5 availability check) ── */

function buildBasePool(allProducts: UnityProduct[]): UnityProduct[] {
  return allProducts.filter((p) => {
    if (p.name.toLowerCase().includes("kleurtester")) return false;
    if (p.stockStatus !== "In stock" && p.stockStatus !== "in_stock") return false;
    if (p.category === "ACCESSOIRES" || p.category === "COSMETICS") return false;
    return true;
  });
}

/* ── Main Component ── */

export function BeautyQuiz({ embedded = false }: { embedded?: boolean } = {}) {
  const t = useTranslations("quiz");
  const [step, setStep] = useState(0); // 0-4 = questions, 5 = results
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState<QuizAnswers>({
    category: null,
    undertone: null,
    look: null,
    sensitivities: [],
    productType: null,
  });

  const allProducts = useMemo(() => getAllProducts(), []);
  const basePool = useMemo(() => buildBasePool(allProducts), [allProducts]);

  const goNext = useCallback(() => {
    setDirection(1);
    setStep((s) => s + 1);
  }, []);

  const goBack = useCallback(() => {
    setDirection(-1);
    setStep((s) => Math.max(0, s - 1));
  }, []);

  const selectCategory = useCallback((cat: string | null) => {
    setAnswers((a) => ({ ...a, category: cat }));
    setDirection(1);
    setStep(1);
  }, []);

  const selectUndertone = useCallback((ut: string | null) => {
    setAnswers((a) => ({ ...a, undertone: ut }));
    setDirection(1);
    setStep(2);
  }, []);

  const selectLook = useCallback((look: string) => {
    setAnswers((a) => ({ ...a, look }));
    setDirection(1);
    setStep(3);
  }, []);

  const toggleSensitivity = useCallback((s: string) => {
    setAnswers((a) => {
      if (s === "none") return { ...a, sensitivities: ["none"] };
      const without = a.sensitivities.filter((x) => x !== "none");
      return {
        ...a,
        sensitivities: without.includes(s)
          ? without.filter((x) => x !== s)
          : [...without, s],
      };
    });
  }, []);

  const selectProductType = useCallback((productType: string) => {
    setAnswers((a) => ({ ...a, productType }));
    setDirection(1);
    setStep(5);
  }, []);

  const restart = useCallback(() => {
    setDirection(-1);
    setStep(0);
    setAnswers({ category: null, undertone: null, look: null, sensitivities: [], productType: null });
  }, []);

  /* ── Compute pool after hard filters (for Q5 availability) ── */

  const poolAfterHardFilters = useMemo(() => {
    let pool = [...basePool];

    // Q1: Category - HARD filter
    if (answers.category) {
      pool = pool.filter((p) => p.category === answers.category);
    }

    // Q4: Sensitivities - HARD filter
    if (answers.sensitivities.length > 0 && !answers.sensitivities.includes("none")) {
      pool = pool.filter((p) => {
        for (const sens of answers.sensitivities) {
          if (sens === "parfum" && !p.classifications.includes("Parfumvrij")) return false;
          if (sens === "nikkel") {
            const has = p.allergenExclusions.some((a) => a.includes("nikkel") || a.includes("kobalt"));
            if (!has) return false;
          }
          if (sens === "lanoline") {
            const has = p.allergenExclusions.some((a) => a.includes("lanoline") || a.includes("perubalsem"));
            if (!has) return false;
          }
          if (sens === "vegan" && !p.classifications.includes("Veganistisch")) return false;
        }
        return true;
      });
    }

    return pool;
  }, [basePool, answers.category, answers.sensitivities]);

  /* ── Q5 availability: count products per type ── */

  const q5Availability = useMemo(() => {
    const individual = poolAfterHardFilters.filter(isIndividualProduct).length;
    const set = poolAfterHardFilters.filter(isSetProduct).length;
    return { individual, set, all: poolAfterHardFilters.length };
  }, [poolAfterHardFilters]);

  /* ── Filter & score products ── */

  const results = useMemo((): ScoredProduct[] => {
    // For "help me choose" mode, curate across categories
    if (!answers.category) {
      return curateHelpMeChoose(basePool, answers, t);
    }

    let pool = [...poolAfterHardFilters];

    // Q5: Product type - HARD filter
    if (answers.productType === "individual") {
      pool = pool.filter(isIndividualProduct);
    } else if (answers.productType === "set") {
      pool = pool.filter(isSetProduct);
    }

    // If hard filters leave zero products, return empty (honest empty state)
    if (pool.length === 0) return [];

    // Score each product
    const scored: ScoredProduct[] = pool.map((product) => {
      let score = 0;
      const reasons: string[] = [];

      // Undertone match
      if (answers.undertone && undertoneMatches(product.undertone, answers.undertone)) {
        score += 30;
        const undertoneLabel = answers.undertone === "koel" ? t("reason.cool") : answers.undertone === "warm" ? t("reason.warm") : t("reason.neutral");
        reasons.push(t("reason.undertoneMatch", { undertone: undertoneLabel }));
      } else if (!answers.undertone && product.undertone?.toLowerCase().includes("neutraal")) {
        // "Weet ik niet" - prefer universal/neutral
        score += 15;
      }

      // Finish match
      if (answers.look && finishMatchesLook(product.finish, answers.look)) {
        score += 20;
        const lookLabel = answers.look === "natural" ? t("reason.natural") : answers.look === "romantic" ? t("reason.romantic") : answers.look === "bold" ? t("reason.bold") : t("reason.colorful");
        const finishLabel = product.finish || "";
        reasons.push(t("reason.finishMatch", { finish: finishLabel, look: lookLabel }));
      } else if (answers.look && !product.finish) {
        // No finish data - neutral, don't penalize
        score += 5;
      }

      // Reviews
      score += Math.min(product.reviewCount, 10);
      score += product.averageRating * 3;

      if (product.reviewCount >= 3 && product.averageRating >= 4.5) {
        reasons.push(t("reason.topRated", { rating: product.averageRating.toFixed(1), count: String(product.reviewCount) }));
      }

      // Allergen safety bonus
      const allergenCount = product.allergenExclusions.length;
      if (allergenCount >= 5 && answers.sensitivities.length > 0 && !answers.sensitivities.includes("none")) {
        reasons.push(t("reason.allergenSafe", { count: String(allergenCount) }));
      }

      // Vegan bonus
      if (product.classifications.includes("Veganistisch")) {
        if (answers.sensitivities.includes("vegan")) {
          reasons.push(t("reason.vegan"));
        }
      }

      // Cap reasons at 2
      const topReasons = reasons.slice(0, 2);

      return { product, score, reasons: topReasons };
    });

    // Sort by score descending
    scored.sort((a, b) => b.score - a.score);

    // Diversify: prefer different subcategories in top results
    return diversifyResults(scored, 6);
  }, [poolAfterHardFilters, basePool, answers, t]);

  const showResults = step === 5;

  return (
    <div className={embedded ? "bg-background py-12 lg:py-20" : "min-h-dvh bg-background flex flex-col"}>
      {/* Header */}
      <div className={embedded ? "content-wrapper w-full" : "content-wrapper w-full pt-6 pb-4"}>
        <div className="flex items-center justify-between mb-6">
          {!embedded ? (
            <Link href="/" className="text-foreground hover:text-text-secondary transition-colors" style={{ transitionDuration: "var(--duration-fast)" }}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </Link>
          ) : <span />}
          <p className="label-sm text-text-secondary">{t("title")}</p>
          {!showResults ? (
            <span className="text-[13px] text-text-tertiary">{step + 1}/{TOTAL_STEPS}</span>
          ) : (
            <button onClick={restart} className="text-[13px] text-cta hover:text-cta/80 transition-colors" style={{ transitionDuration: "var(--duration-fast)" }}>
              {t("restart")}
            </button>
          )}
        </div>

        {/* Progress bar */}
        {!showResults && (
          <div className="w-full h-1 bg-border rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-foreground rounded-full"
              initial={false}
              animate={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>
        )}
      </div>

      {/* Content area */}
      <div className={embedded ? "" : "flex-1 flex flex-col"}>
        <AnimatePresence mode="wait" custom={direction}>
          {step === 0 && (
            <QuizStep key="q1" direction={direction}>
              <QuestionCategory t={t} onSelect={selectCategory} />
            </QuizStep>
          )}
          {step === 1 && (
            <QuizStep key="q2" direction={direction}>
              <QuestionUndertone t={t} onSelect={selectUndertone} onBack={goBack} />
            </QuizStep>
          )}
          {step === 2 && (
            <QuizStep key="q3" direction={direction}>
              <QuestionLook t={t} onSelect={selectLook} onBack={goBack} />
            </QuizStep>
          )}
          {step === 3 && (
            <QuizStep key="q4" direction={direction}>
              <QuestionSensitivities
                t={t}
                selected={answers.sensitivities}
                onToggle={toggleSensitivity}
                onNext={goNext}
                onBack={goBack}
              />
            </QuizStep>
          )}
          {step === 4 && (
            <QuizStep key="q5" direction={direction}>
              <QuestionProductType
                t={t}
                onSelect={selectProductType}
                onBack={goBack}
                availability={q5Availability}
              />
            </QuizStep>
          )}
          {showResults && (
            <QuizStep key="results" direction={direction}>
              <Results t={t} results={results} answers={answers} onRestart={restart} />
            </QuizStep>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ── "Help me choose" curation ── */

function curateHelpMeChoose(
  basePool: UnityProduct[],
  answers: QuizAnswers,
  t: ReturnType<typeof useTranslations>,
): ScoredProduct[] {
  // Apply sensitivities as hard filter
  let pool = [...basePool];
  if (answers.sensitivities.length > 0 && !answers.sensitivities.includes("none")) {
    pool = pool.filter((p) => {
      for (const sens of answers.sensitivities) {
        if (sens === "parfum" && !p.classifications.includes("Parfumvrij")) return false;
        if (sens === "nikkel") {
          const has = p.allergenExclusions.some((a) => a.includes("nikkel") || a.includes("kobalt"));
          if (!has) return false;
        }
        if (sens === "lanoline") {
          const has = p.allergenExclusions.some((a) => a.includes("lanoline") || a.includes("perubalsem"));
          if (!has) return false;
        }
        if (sens === "vegan" && !p.classifications.includes("Veganistisch")) return false;
      }
      return true;
    });
  }

  if (pool.length === 0) return [];

  const scoreProduct = (p: UnityProduct): { score: number; reasons: string[] } => {
    let score = 0;
    const reasons: string[] = [];

    if (answers.undertone && undertoneMatches(p.undertone, answers.undertone)) {
      score += 30;
      const undertoneLabel = answers.undertone === "koel" ? t("reason.cool") : answers.undertone === "warm" ? t("reason.warm") : t("reason.neutral");
      reasons.push(t("reason.undertoneMatch", { undertone: undertoneLabel }));
    } else if (!answers.undertone && p.undertone?.toLowerCase().includes("neutraal")) {
      score += 15;
    }

    if (answers.look && finishMatchesLook(p.finish, answers.look)) {
      score += 20;
      const lookLabel = answers.look === "natural" ? t("reason.natural") : answers.look === "romantic" ? t("reason.romantic") : answers.look === "bold" ? t("reason.bold") : t("reason.colorful");
      reasons.push(t("reason.finishMatch", { finish: p.finish || "", look: lookLabel }));
    }

    score += Math.min(p.reviewCount, 10);
    score += p.averageRating * 3;

    if (p.reviewCount >= 3 && p.averageRating >= 4.5) {
      reasons.push(t("reason.topRated", { rating: p.averageRating.toFixed(1), count: String(p.reviewCount) }));
    }

    if (p.classifications.includes("Veganistisch") && answers.sensitivities.includes("vegan")) {
      reasons.push(t("reason.vegan"));
    }

    return { score, reasons: reasons.slice(0, 2) };
  };

  const results: ScoredProduct[] = [];
  const usedSkus = new Set<string>();

  const pickBest = (candidates: UnityProduct[]): ScoredProduct | null => {
    const available = candidates.filter((p) => !usedSkus.has(p.sku));
    if (available.length === 0) return null;

    let best: { product: UnityProduct; score: number; reasons: string[] } | null = null;
    for (const p of available) {
      const { score, reasons } = scoreProduct(p);
      if (!best || score > best.score) {
        best = { product: p, score, reasons };
      }
    }
    return best;
  };

  // 1. Best face product matching undertone
  const faceProducts = pool.filter((p) => p.category === "GEZICHT");
  const bestFace = pickBest(faceProducts);
  if (bestFace) {
    usedSkus.add(bestFace.product.sku);
    results.push(bestFace);
  }

  // 2. Best eye product matching look
  const eyeIndividuals = pool.filter((p) => p.category === "OGEN" && isIndividualProduct(p));
  const bestEye = pickBest(eyeIndividuals);
  if (bestEye) {
    usedSkus.add(bestEye.product.sku);
    results.push(bestEye);
  }

  // 3. Best palette matching look
  const eyePalettes = pool.filter((p) => p.category === "OGEN" && isSetProduct(p));
  const bestPalette = pickBest(eyePalettes);
  if (bestPalette) {
    usedSkus.add(bestPalette.product.sku);
    results.push(bestPalette);
  }

  // 4. Best lip product matching undertone
  const lipProducts = pool.filter((p) => p.category === "LIPPEN");
  const bestLip = pickBest(lipProducts);
  if (bestLip) {
    usedSkus.add(bestLip.product.sku);
    results.push(bestLip);
  }

  // 5. Most reviewed product overall (social proof)
  const byReviews = pool
    .filter((p) => !usedSkus.has(p.sku) && p.reviewCount > 0)
    .sort((a, b) => b.reviewCount - a.reviewCount);
  if (byReviews.length > 0) {
    const p = byReviews[0];
    const { score, reasons } = scoreProduct(p);
    reasons.unshift(t("reason.socialProof", { count: String(p.reviewCount) }));
    usedSkus.add(p.sku);
    results.push({ product: p, score, reasons: reasons.slice(0, 2) });
  }

  // 6. Wildcard: highest rated product not yet selected
  const byRating = pool
    .filter((p) => !usedSkus.has(p.sku) && p.averageRating > 0)
    .sort((a, b) => {
      if (b.averageRating !== a.averageRating) return b.averageRating - a.averageRating;
      return b.reviewCount - a.reviewCount;
    });
  if (byRating.length > 0) {
    const p = byRating[0];
    const { score, reasons } = scoreProduct(p);
    reasons.unshift(t("reason.wildcard"));
    usedSkus.add(p.sku);
    results.push({ product: p, score, reasons: reasons.slice(0, 2) });
  }

  return results;
}

/* ── Diversify results across subcategories ── */

function diversifyResults(scored: ScoredProduct[], limit: number): ScoredProduct[] {
  if (scored.length <= limit) return scored;

  const results: ScoredProduct[] = [];
  const subcategoryCounts = new Map<string, number>();

  // First pass: pick top items while ensuring subcategory diversity
  for (const item of scored) {
    if (results.length >= limit) break;

    const sub = item.product.subcategory;
    const currentCount = subcategoryCounts.get(sub) || 0;

    // Allow max 2 products per subcategory in top results
    if (currentCount < 2) {
      results.push(item);
      subcategoryCounts.set(sub, currentCount + 1);
    }
  }

  // Second pass: if we don't have enough, fill with highest-scored remaining
  if (results.length < limit) {
    const usedSkus = new Set(results.map((r) => r.product.sku));
    for (const item of scored) {
      if (results.length >= limit) break;
      if (!usedSkus.has(item.product.sku)) {
        results.push(item);
      }
    }
  }

  return results;
}

/* ── Step wrapper ── */

function QuizStep({ children, direction }: { children: React.ReactNode; direction: number }) {
  return (
    <motion.div
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={transition}
      className="content-wrapper w-full flex-1 flex flex-col"
    >
      {children}
    </motion.div>
  );
}

/* ── Q1: Category ── */

function QuestionCategory({ t, onSelect }: { t: ReturnType<typeof useTranslations>; onSelect: (cat: string | null) => void }) {
  const options = [
    { label: t("q1.face"), value: "GEZICHT", icon: FaceIcon },
    { label: t("q1.eyes"), value: "OGEN", icon: EyeIcon },
    { label: t("q1.lips"), value: "LIPPEN", icon: LipIcon },
    { label: t("q1.helpMe"), value: null, icon: SparkleIcon },
  ];

  return (
    <div className="py-8 lg:py-16 flex flex-col items-center">
      {/* h1, not h2: this is the quiz landing state (step 0) and the only
          heading in the prerendered HTML, so it carries the page topic for
          search engines. Later steps keep h2 and only ever render
          client-side, so the page never shows two h1s. */}
      <h1 className="font-[family-name:var(--font-cormorant)] text-foreground text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-light text-center leading-tight mb-3">
        {t("q1.title")}
      </h1>
      <p className="text-text-secondary text-[14px] text-center mb-10 max-w-md">{t("q1.subtitle")}</p>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-lg">
        {options.map((opt) => (
          <button
            key={opt.label}
            onClick={() => onSelect(opt.value)}
            className="flex flex-col items-center justify-center gap-3 p-6 sm:p-8 bg-surface rounded-2xl border border-transparent hover:border-foreground/15 transition-all active:scale-[0.98]"
            style={{ transitionDuration: "var(--duration-normal)", transitionTimingFunction: "var(--ease-out)" }}
          >
            <opt.icon />
            <span className="text-[14px] sm:text-[15px] font-medium text-foreground">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Q2: Undertone ── */

function QuestionUndertone({ t, onSelect, onBack }: { t: ReturnType<typeof useTranslations>; onSelect: (ut: string | null) => void; onBack: () => void }) {
  const options = [
    { label: t("q2.cool"), value: "koel", colors: ["#D4A0C0", "#C4A0D0"] },
    { label: t("q2.warm"), value: "warm", colors: ["#D4A574", "#C4956A"] },
    { label: t("q2.neutral"), value: "neutraal", colors: ["#C8B8A8", "#B8A898"] },
    { label: t("q2.dontKnow"), value: null, colors: [] },
  ];

  return (
    <div className="py-8 lg:py-16 flex flex-col items-center">
      <h2 className="font-[family-name:var(--font-cormorant)] text-foreground text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-light text-center leading-tight mb-3">
        {t("q2.title")}
      </h2>
      <p className="text-text-secondary text-[14px] text-center mb-10 max-w-md">{t("q2.subtitle")}</p>

      <div className="flex flex-col gap-3 w-full max-w-md">
        {options.map((opt) => (
          <button
            key={opt.label}
            onClick={() => onSelect(opt.value)}
            className="flex items-center gap-4 p-5 bg-surface rounded-2xl border border-transparent hover:border-foreground/15 transition-all active:scale-[0.98] text-left"
            style={{ transitionDuration: "var(--duration-normal)", transitionTimingFunction: "var(--ease-out)" }}
          >
            {opt.colors.length > 0 ? (
              <div className="flex gap-1.5">
                {opt.colors.map((c, i) => (
                  <span key={i} className="w-8 h-8 rounded-full border border-border" style={{ backgroundColor: c }} />
                ))}
              </div>
            ) : (
              <span className="w-8 h-8 rounded-full border-2 border-dashed border-text-secondary/40 flex items-center justify-center">
                <span className="text-text-secondary text-[14px]">?</span>
              </span>
            )}
            <div>
              <span className="text-[14px] sm:text-[15px] font-medium text-foreground block">{opt.label}</span>
              {opt.value === "koel" && <span className="text-[12px] text-text-secondary">{t("q2.coolHint")}</span>}
              {opt.value === "warm" && <span className="text-[12px] text-text-secondary">{t("q2.warmHint")}</span>}
              {opt.value === "neutraal" && <span className="text-[12px] text-text-secondary">{t("q2.neutralHint")}</span>}
            </div>
          </button>
        ))}
      </div>

      {/* Visual hint */}
      <div className="mt-8 p-4 bg-surface/60 rounded-xl max-w-md w-full">
        <p className="text-[12px] text-text-secondary text-center">{t("q2.hint")}</p>
      </div>

      <BackButton onClick={onBack} t={t} />
    </div>
  );
}

/* ── Q3: Look ── */

function QuestionLook({ t, onSelect, onBack }: { t: ReturnType<typeof useTranslations>; onSelect: (look: string) => void; onBack: () => void }) {
  const options = [
    { label: t("q3.natural"), value: "natural", bg: "bg-[#F5F0E8]", accent: "text-[#8B7355]" },
    { label: t("q3.romantic"), value: "romantic", bg: "bg-[#F0E8EF]", accent: "text-[#8B5580]" },
    { label: t("q3.bold"), value: "bold", bg: "bg-[#2A2A2A]", accent: "text-[#D4A574]" },
    { label: t("q3.colorful"), value: "colorful", bg: "bg-[#E8F0F0]", accent: "text-[#4A7B7B]" },
  ];

  return (
    <div className="py-8 lg:py-16 flex flex-col items-center">
      <h2 className="font-[family-name:var(--font-cormorant)] text-foreground text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-light text-center leading-tight mb-3">
        {t("q3.title")}
      </h2>
      <p className="text-text-secondary text-[14px] text-center mb-10 max-w-md">{t("q3.subtitle")}</p>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-lg">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            className={`flex flex-col items-center justify-center gap-2 p-6 sm:p-8 rounded-2xl border border-transparent hover:border-foreground/15 transition-all active:scale-[0.98] ${opt.bg}`}
            style={{ transitionDuration: "var(--duration-normal)", transitionTimingFunction: "var(--ease-out)" }}
          >
            <span className={`text-[14px] sm:text-[15px] font-medium ${opt.value === "bold" ? "text-background" : "text-foreground"}`}>
              {opt.label}
            </span>
            <span className={`text-[12px] ${opt.value === "bold" ? "text-background/60" : "text-text-secondary"}`}>
              {opt.value === "natural" && t("q3.naturalDesc")}
              {opt.value === "romantic" && t("q3.romanticDesc")}
              {opt.value === "bold" && t("q3.boldDesc")}
              {opt.value === "colorful" && t("q3.colorfulDesc")}
            </span>
          </button>
        ))}
      </div>

      <BackButton onClick={onBack} t={t} />
    </div>
  );
}

/* ── Q4: Sensitivities (multi-select) ── */

function QuestionSensitivities({
  t,
  selected,
  onToggle,
  onNext,
  onBack,
}: {
  t: ReturnType<typeof useTranslations>;
  selected: string[];
  onToggle: (s: string) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const options = [
    { label: t("q4.fragrance"), value: "parfum" },
    { label: t("q4.nickel"), value: "nikkel" },
    { label: t("q4.lanolin"), value: "lanoline" },
    { label: t("q4.vegan"), value: "vegan" },
    { label: t("q4.none"), value: "none" },
  ];

  return (
    <div className="py-8 lg:py-16 flex flex-col items-center">
      <h2 className="font-[family-name:var(--font-cormorant)] text-foreground text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-light text-center leading-tight mb-3">
        {t("q4.title")}
      </h2>
      <p className="text-text-secondary text-[14px] text-center mb-10 max-w-md">{t("q4.subtitle")}</p>

      <div className="flex flex-col gap-3 w-full max-w-md">
        {options.map((opt) => {
          const isSelected = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              onClick={() => onToggle(opt.value)}
              className={`flex items-center gap-4 p-5 rounded-2xl border transition-all active:scale-[0.98] text-left ${
                isSelected
                  ? "bg-cta/8 border-cta/30"
                  : "bg-surface border-transparent hover:border-foreground/15"
              }`}
              style={{ transitionDuration: "var(--duration-normal)", transitionTimingFunction: "var(--ease-out)" }}
            >
              <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected ? "border-cta bg-cta" : "border-text-secondary/30"
              }`}>
                {isSelected && (
                  <svg className="w-3 h-3 text-background" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8.5l3.5 3.5 6.5-8" />
                  </svg>
                )}
              </span>
              <span className="text-[14px] sm:text-[15px] font-medium text-foreground">{opt.label}</span>
            </button>
          );
        })}
      </div>

      <p className="text-[12px] text-text-secondary text-center mt-6 max-w-sm">{t("q4.note")}</p>

      <button
        onClick={onNext}
        disabled={selected.length === 0}
        className="mt-8 inline-flex items-center justify-center h-12 px-10 bg-foreground text-background label-sm hover:bg-foreground/85 transition-colors rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
        style={{ transitionDuration: "var(--duration-fast)", transitionTimingFunction: "var(--ease-out)" }}
      >
        {t("next")}
      </button>

      <BackButton onClick={onBack} t={t} />
    </div>
  );
}

/* ── Q5: Product Type (replaces budget) ── */

function QuestionProductType({
  t,
  onSelect,
  onBack,
  availability,
}: {
  t: ReturnType<typeof useTranslations>;
  onSelect: (type: string) => void;
  onBack: () => void;
  availability: { individual: number; set: number; all: number };
}) {
  const options = [
    {
      label: t("q5.individual"),
      desc: t("q5.individualDesc"),
      value: "individual",
      count: availability.individual,
    },
    {
      label: t("q5.set"),
      desc: t("q5.setDesc"),
      value: "set",
      count: availability.set,
    },
    {
      label: t("q5.all"),
      desc: t("q5.allDesc"),
      value: "all",
      count: availability.all,
    },
  ];

  // Only show options that have products
  const visibleOptions = options.filter((opt) => opt.count > 0);

  return (
    <div className="py-8 lg:py-16 flex flex-col items-center">
      <h2 className="font-[family-name:var(--font-cormorant)] text-foreground text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-light text-center leading-tight mb-3">
        {t("q5.title")}
      </h2>
      <p className="text-text-secondary text-[14px] text-center mb-10 max-w-md">{t("q5.subtitle")}</p>

      <div className="flex flex-col gap-3 w-full max-w-md">
        {visibleOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            className="flex items-center justify-between p-5 bg-surface rounded-2xl border border-transparent hover:border-foreground/15 transition-all active:scale-[0.98] text-left"
            style={{ transitionDuration: "var(--duration-normal)", transitionTimingFunction: "var(--ease-out)" }}
          >
            <div>
              <span className="text-[14px] sm:text-[15px] font-medium text-foreground block">{opt.label}</span>
              <span className="text-[12px] text-text-secondary">{opt.desc}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
              <span className="text-[12px] text-text-tertiary">{opt.count}</span>
              <svg className="w-4 h-4 text-text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      <BackButton onClick={onBack} t={t} />
    </div>
  );
}

/* ── Results ── */

function Results({
  t,
  results,
  answers,
  onRestart,
}: {
  t: ReturnType<typeof useTranslations>;
  results: ScoredProduct[];
  answers: QuizAnswers;
  onRestart: () => void;
}) {
  const { addItem, openDrawer } = useCart();

  const handleAddToCart = useCallback((product: UnityProduct) => {
    // Prefer canonical DB slug — see same fix in product-detail.tsx:535.
    addItem({
      slug: product.slug || buildSlug(product.name),
      name: product.name,
      price: parseFloat(product.price),
      image: product.images[0] || "",
      sku: product.sku,
    });
    openDrawer();
  }, [addItem, openDrawer]);

  // Build allergen badges
  const buildAllergenBadges = useCallback((product: UnityProduct) => {
    const badges: string[] = [];
    if (product.classifications.includes("Parfumvrij")) badges.push(t("badge.fragranceFree"));
    if (product.allergenExclusions.some((a) => a.includes("nikkel"))) badges.push(t("badge.nickelFree"));
    if (product.allergenExclusions.some((a) => a.includes("lanoline"))) badges.push(t("badge.lanolinFree"));
    if (product.classifications.includes("Veganistisch")) badges.push(t("badge.vegan"));
    if (product.classifications.includes("Hypoallergeen")) badges.push(t("badge.hypoallergenic"));
    return badges;
  }, [t]);

  // Empty state
  if (results.length === 0) {
    return (
      <div className="py-8 lg:py-12 content-wrapper">
        <div className="text-center mb-10">
          <p className="label-sm text-cta mb-3">{t("results.label")}</p>
          <h2 className="font-[family-name:var(--font-cormorant)] text-foreground text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-light leading-tight mb-3">
            {t("results.emptyTitle")}
          </h2>
          <p className="text-text-secondary text-[14px] max-w-md mx-auto mb-8">{t("results.emptyText")}</p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={onRestart}
              className="inline-flex items-center justify-center h-10 px-6 bg-foreground text-background text-[12px] font-semibold tracking-wider uppercase hover:bg-foreground/85 transition-colors rounded-lg"
              style={{ transitionDuration: "var(--duration-fast)" }}
            >
              {t("results.tryAgain")}
            </button>
            <a
              href="https://wa.me/31612345678"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-10 px-6 border border-foreground/20 text-foreground text-[12px] font-semibold tracking-wider uppercase hover:border-foreground/40 transition-colors rounded-lg"
              style={{ transitionDuration: "var(--duration-fast)" }}
            >
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Determine collection link based on category
  const collectionPath = answers.category
    ? `/collecties/${answers.category === "GEZICHT" ? "gezicht" : answers.category === "OGEN" ? "ogen" : "lippen"}`
    : "/collecties/ogen";

  return (
    <div className="py-8 lg:py-12 content-wrapper">
      {/* Results header */}
      <div className="text-center mb-10">
        <p className="label-sm text-cta mb-3">{t("results.label")}</p>
        <h2 className="font-[family-name:var(--font-cormorant)] text-foreground text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-light leading-tight mb-3">
          {t("results.title")}
        </h2>
        <p className="text-text-secondary text-[14px] max-w-md mx-auto">{t("results.subtitle", { count: results.length })}</p>
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-16">
        {results.map(({ product, reasons }) => {
          const slug = buildSlug(product.name);
          const price = parseFloat(product.price);
          const badges = buildAllergenBadges(product);

          return (
            <motion.div
              key={product.sku}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col"
            >
              {/* Image */}
              <Link href={`/producten/${slug}`} className="relative bg-surface aspect-square overflow-hidden mb-4 rounded-2xl">
                {product.images[0] ? (
                  <Image
                    src={product.images[0]}
                    alt={product.name}
                    fill
                    sizes="(max-width: 640px) 50vw, 25vw"
                    className="object-contain p-6"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-10 h-10 text-border" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </div>
                )}
              </Link>

              {/* Content */}
              <div className="flex flex-col flex-1">
                {/* Ratings */}
                {product.reviewCount > 0 && (
                  <div className="mb-2">
                    <Stars rating={product.averageRating} count={product.reviewCount} />
                  </div>
                )}

                {/* Name */}
                <Link href={`/producten/${slug}`} className="text-[15px] font-medium text-foreground mb-1.5 hover:text-text-secondary transition-colors line-clamp-2" style={{ transitionDuration: "var(--duration-fast)" }}>
                  {product.name}
                </Link>

                {/* Personalized reasons */}
                {reasons.length > 0 && (
                  <div className="mb-2">
                    {reasons.map((reason, i) => (
                      <p key={i} className="text-[12px] text-cta">{reason}</p>
                    ))}
                  </div>
                )}

                {/* Allergen badges */}
                {badges.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {badges.slice(0, 3).map((badge) => (
                      <span key={badge} className="inline-flex items-center gap-1 text-[11px] text-cta bg-cta/8 px-2 py-0.5 rounded-md">
                        <svg className="w-2.5 h-2.5 flex-shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 8.5l3.5 3.5 6.5-8" />
                        </svg>
                        {badge}
                      </span>
                    ))}
                  </div>
                )}

                {/* Price + Add to cart */}
                <div className="mt-auto flex items-center justify-between gap-2 pt-2">
                  <span className="text-[15px] font-semibold text-foreground">{fmt.format(price)}</span>
                  <button
                    onClick={() => handleAddToCart(product)}
                    className="inline-flex items-center justify-center h-9 px-4 bg-foreground text-background text-[12px] font-semibold tracking-wider uppercase hover:bg-foreground/85 transition-colors rounded-lg active:scale-[0.98]"
                    style={{ transitionDuration: "var(--duration-fast)" }}
                  >
                    {t("results.addToCart")}
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Footer section */}
      <div className="bg-surface rounded-2xl p-8 sm:p-10 text-center max-w-lg mx-auto">
        <h3 className="text-[15px] font-semibold text-foreground mb-2">{t("results.noMatch")}</h3>
        <p className="text-[13px] text-text-secondary mb-6">{t("results.noMatchText")}</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            href={collectionPath}
            className="inline-flex items-center justify-center h-10 px-6 bg-foreground text-background text-[12px] font-semibold tracking-wider uppercase hover:bg-foreground/85 transition-colors rounded-lg"
            style={{ transitionDuration: "var(--duration-fast)" }}
          >
            {t("results.viewCollection")}
          </Link>
          <a
            href="https://wa.me/31612345678"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-10 px-6 border border-foreground/20 text-foreground text-[12px] font-semibold tracking-wider uppercase hover:border-foreground/40 transition-colors rounded-lg"
            style={{ transitionDuration: "var(--duration-fast)" }}
          >
            WhatsApp
          </a>
        </div>
        <button
          onClick={onRestart}
          className="mt-4 text-[13px] text-text-secondary hover:text-foreground transition-colors underline underline-offset-4"
          style={{ transitionDuration: "var(--duration-fast)" }}
        >
          {t("results.retake")}
        </button>
      </div>
    </div>
  );
}

/* ── Back button ── */

function BackButton({ onClick, t }: { onClick: () => void; t: ReturnType<typeof useTranslations> }) {
  return (
    <button
      onClick={onClick}
      className="mt-8 text-[13px] text-text-secondary hover:text-foreground transition-colors"
      style={{ transitionDuration: "var(--duration-fast)" }}
    >
      {t("back")}
    </button>
  );
}

/* ── Icons ── */

function FaceIcon() {
  return (
    <svg className="w-8 h-8 text-foreground" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="16" cy="16" r="12" />
      <circle cx="12" cy="14" r="1" fill="currentColor" stroke="none" />
      <circle cx="20" cy="14" r="1" fill="currentColor" stroke="none" />
      <path d="M12 20c1.5 2 6.5 2 8 0" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg className="w-8 h-8 text-foreground" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M4 16s5-8 12-8 12 8 12 8-5 8-12 8S4 16 4 16z" />
      <circle cx="16" cy="16" r="4" />
      <circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function LipIcon() {
  return (
    <svg className="w-8 h-8 text-foreground" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M6 16c0 0 4 8 10 8s10-8 10-8" />
      <path d="M6 16c0 0 4-4 10-4s10 4 10 4" />
      <path d="M16 12v0c-1.5 2-1.5 2 0 4 1.5-2 1.5-2 0-4z" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg className="w-8 h-8 text-foreground" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4v6M16 22v6M4 16h6M22 16h6M7.5 7.5l4 4M20.5 20.5l4 4M24.5 7.5l-4 4M11.5 20.5l-4 4" />
    </svg>
  );
}
